import React, { useState, useMemo } from 'react';
import {
  Plus,
  Trash2,
  Save,
  ChevronUp,
  ChevronDown,
  Eye,
  EyeOff,
  Filter,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useFirestoreCollection } from '../../hooks/useFirestoreCollection';
import { useStorage } from '../../hooks/useStorage';
import { FirebaseImage } from '../FirebaseImage';
import {
  addDocument,
  updateDocument,
  deleteDocument,
} from '../../lib/firestore-helpers';
import type { GalleryItem } from '../../types/firebase';

const LANGS = ['fr', 'de', 'en', 'pt'] as const;
const LANG_LABELS: Record<string, string> = {
  fr: 'Francais',
  de: 'Deutsch',
  en: 'English',
  pt: 'Portugues',
};

interface GalleryForm {
  title: Record<string, string>;
  description: Record<string, string>;
  tags: string;
  album: string;
  published: boolean;
  order: number;
}

const emptyForm = (): GalleryForm => ({
  title: { fr: '', de: '', en: '', pt: '' },
  description: { fr: '', de: '', en: '', pt: '' },
  tags: '',
  album: '',
  published: true,
  order: 0,
});

const GalleryTab: React.FC = () => {
  const { data: items } = useFirestoreCollection<GalleryItem>({
    collection: 'gallery',
    orderBy: ['order', 'asc'],
    realtime: true,
  });
  const { uploadFile, uploading, progress } = useStorage();

  const [editing, setEditing] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState<GalleryForm>(emptyForm());
  const [newFile, setNewFile] = useState<File | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [albumFilter, setAlbumFilter] = useState<string>('');

  // Collect unique albums
  const albums = useMemo(() => {
    const set = new Set<string>();
    items.forEach((item) => {
      if (item.album) set.add(item.album);
    });
    return Array.from(set).sort();
  }, [items]);

  const filteredItems = useMemo(() => {
    const sorted = [...items].sort((a, b) => a.order - b.order);
    if (!albumFilter) return sorted;
    return sorted.filter((i) => i.album === albumFilter);
  }, [items, albumFilter]);

  const startAdd = () => {
    setEditing(null);
    setForm({
      ...emptyForm(),
      order: items.length > 0 ? Math.max(...items.map((i) => i.order)) + 1 : 0,
    });
    setNewFile(null);
    setShowAdd(true);
  };

  const startEdit = (item: GalleryItem) => {
    setShowAdd(false);
    setEditing(item.id);
    setForm({
      title: { fr: item.title?.fr || '', de: item.title?.de || '', en: item.title?.en || '', pt: item.title?.pt || '' },
      description: { fr: item.description?.fr || '', de: item.description?.de || '', en: item.description?.en || '', pt: item.description?.pt || '' },
      tags: (item.tags || []).join(', '),
      album: item.album || '',
      published: item.published,
      order: item.order,
    });
    setNewFile(null);
  };

  const handleSave = async () => {
    const tags = form.tags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);

    if (editing) {
      // Update existing
      try {
        const updates: Record<string, unknown> = {
          title: form.title,
          description: form.description,
          tags,
          album: form.album,
          published: form.published,
          order: form.order,
        };

        // If new image provided
        if (newFile) {
          const path = `gallery/${Date.now()}_${newFile.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
          const { url, thumbnailUrl } = await uploadFile(newFile, path);
          updates.imageUrl = url;
          updates.thumbnailUrl = thumbnailUrl;
        }

        await updateDocument('gallery', editing, updates);
        toast.success('Image galerie mise a jour');
        setEditing(null);
      } catch {
        toast.error('Erreur mise a jour');
      }
    } else {
      // Add new
      if (!newFile) {
        toast.error('Une image est obligatoire');
        return;
      }
      try {
        const path = `gallery/${Date.now()}_${newFile.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
        const { url, thumbnailUrl } = await uploadFile(newFile, path);
        await addDocument('gallery', {
          title: form.title,
          description: form.description,
          tags,
          album: form.album,
          imageUrl: url,
          thumbnailUrl,
          published: form.published,
          order: form.order,
        });
        toast.success('Image ajoutee a la galerie');
        setShowAdd(false);
      } catch {
        toast.error('Erreur ajout');
      }
    }
    setNewFile(null);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteDocument('gallery', id);
      toast.success('Image supprimee');
      setDeleteConfirm(null);
      if (editing === id) setEditing(null);
    } catch {
      toast.error('Erreur suppression');
    }
  };

  const handleTogglePublished = async (item: GalleryItem) => {
    try {
      await updateDocument('gallery', item.id, { published: !item.published });
    } catch {
      toast.error('Erreur mise a jour');
    }
  };

  const handleReorder = async (item: GalleryItem, direction: 'up' | 'down') => {
    const sorted = [...items].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex((i) => i.id === item.id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    try {
      await updateDocument('gallery', sorted[idx].id, { order: sorted[swapIdx].order });
      await updateDocument('gallery', sorted[swapIdx].id, { order: sorted[idx].order });
    } catch {
      toast.error('Erreur reordonnement');
    }
  };

  const renderForm = () => (
    <div className="bg-gray-50 rounded-xl p-4 space-y-4 border border-gray-200">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Image</label>
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={(e) => setNewFile(e.target.files?.[0] || null)}
          className="text-sm"
        />
        {uploading && (
          <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
            <div className="bg-red-600 h-1.5 rounded-full" style={{ width: `${progress}%` }} />
          </div>
        )}
      </div>

      {LANGS.map((lang) => (
        <div key={`title-${lang}`} className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-700 mb-1">Titre ({LANG_LABELS[lang]})</label>
            <input
              value={form.title[lang] || ''}
              onChange={(e) => setForm({ ...form, title: { ...form.title, [lang]: e.target.value } })}
              className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Description ({LANG_LABELS[lang]})</label>
            <input
              value={form.description[lang] || ''}
              onChange={(e) => setForm({ ...form, description: { ...form.description, [lang]: e.target.value } })}
              className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:outline-none"
            />
          </div>
        </div>
      ))}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-700 mb-1">Tags (separes par des virgules)</label>
          <input
            value={form.tags}
            onChange={(e) => setForm({ ...form, tags: e.target.value })}
            className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:outline-none"
            placeholder="pneus, custom, mecanique"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-700 mb-1">Album</label>
          <input
            value={form.album}
            onChange={(e) => setForm({ ...form, album: e.target.value })}
            className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:outline-none"
            placeholder="general"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-700 mb-1">Ordre</label>
          <input
            type="number"
            value={form.order}
            onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })}
            className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:outline-none"
          />
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.published}
              onChange={(e) => setForm({ ...form, published: e.target.checked })}
              className="rounded border-gray-300 text-red-600 focus:ring-red-500"
            />
            <span className="text-sm text-gray-700">Publie</span>
          </label>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <button
          onClick={() => { setEditing(null); setShowAdd(false); }}
          className="px-4 py-2 text-sm bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
        >
          Annuler
        </button>
        <button
          onClick={handleSave}
          disabled={uploading}
          className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-1 disabled:opacity-50"
        >
          <Save className="w-4 h-4" /> Sauvegarder
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Galerie</h2>
        <div className="flex items-center gap-3">
          {/* Album filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <select
              value={albumFilter}
              onChange={(e) => setAlbumFilter(e.target.value)}
              className="bg-gray-100 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <option value="">Tous les albums</option>
              {albums.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={startAdd}
            className="flex items-center gap-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm transition-colors"
          >
            <Plus className="w-4 h-4" /> Ajouter
          </button>
        </div>
      </div>

      {showAdd && renderForm()}

      {filteredItems.length === 0 && !showAdd && (
        <p className="text-gray-500 text-center py-8">Aucune image dans la galerie</p>
      )}

      {filteredItems.map((item) => (
        <div key={item.id}>
          <div className="bg-white rounded-xl shadow p-4 flex items-center gap-4">
            <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
              <FirebaseImage
                src={item.thumbnailUrl || item.imageUrl}
                alt={item.title?.fr || ''}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900 truncate">
                {item.title?.fr || item.title?.en || '(sans titre)'}
              </p>
              <div className="flex gap-1 mt-1 flex-wrap">
                {(item.tags || []).map((tag) => (
                  <span key={tag} className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
              {item.album && (
                <p className="text-xs text-gray-400 mt-1">Album: {item.album}</p>
              )}
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => handleTogglePublished(item)}
                className={`rounded-lg p-2 ${item.published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}
              >
                {item.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>
              <button onClick={() => handleReorder(item, 'up')} className="bg-gray-100 hover:bg-gray-200 rounded-lg p-2">
                <ChevronUp className="w-4 h-4 text-gray-600" />
              </button>
              <button onClick={() => handleReorder(item, 'down')} className="bg-gray-100 hover:bg-gray-200 rounded-lg p-2">
                <ChevronDown className="w-4 h-4 text-gray-600" />
              </button>
              <button onClick={() => startEdit(item)} className="bg-blue-100 hover:bg-blue-200 rounded-lg p-2 text-blue-700">
                <Save className="w-4 h-4" />
              </button>
              <button onClick={() => setDeleteConfirm(item.id)} className="bg-red-100 hover:bg-red-200 rounded-lg p-2 text-red-700">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          {deleteConfirm === item.id && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-2 flex items-center justify-between">
              <span className="text-sm text-red-700">Confirmer la suppression ?</span>
              <div className="flex gap-2">
                <button onClick={() => handleDelete(item.id)} className="bg-red-600 text-white text-xs px-3 py-1 rounded hover:bg-red-700">
                  Supprimer
                </button>
                <button onClick={() => setDeleteConfirm(null)} className="bg-gray-200 text-gray-700 text-xs px-3 py-1 rounded hover:bg-gray-300">
                  Annuler
                </button>
              </div>
            </div>
          )}

          {editing === item.id && <div className="mt-2">{renderForm()}</div>}
        </div>
      ))}
    </div>
  );
};

export default GalleryTab;
